import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Bell, CheckCircle, Clock, Copy, CreditCard, Database, History, Home,
  Loader2, LogOut, Map, MessageSquare, Network, Newspaper, Play, Plus, Save,
  Send, Server, Settings, Square, Trash2, TrendingUp, Users, Wrench,
  XCircle, TestTube
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ktcLogo from '../../assets/ktc.png';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import StaffPageHeader from '../../components/StaffPageHeader';

const API = `${resolveBackendBaseUrl()}/api`;

const toArray = (value) => (Array.isArray(value) ? value : []);

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('id-ID');
};

const normalizeSchedulerStatus = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return {
    running: Boolean(source.running),
    jobs: toArray(source.jobs).filter((item) => item && typeof item === 'object'),
  };
};

const DEFAULT_FREERADIUS_MAPPINGS = {
  reply_attribute: 'Mikrotik-Rate-Limit',
  reply_operator: ':=',
  prefer_package_speed: true,
  items: [
    { id: 'family-default', package_key: 'family', package_name: 'Family', rate_limit: '10M/10M', enabled: true },
    { id: 'streaming-default', package_key: 'streaming', package_name: 'Streaming', rate_limit: '20M/20M', enabled: true },
    { id: 'flash-default', package_key: 'flash', package_name: 'Flash', rate_limit: '30M/30M', enabled: true },
  ],
};

const DEFAULT_FREERADIUS_TUNNEL = {
  ssh_user: '',
  ssh_host: '',
  ssh_port: 22,
  local_port: 3307,
  remote_db_host: '127.0.0.1',
  remote_db_port: 3306,
  identity_file: '',
  session_name: '',
};

const DEFAULT_GENIEACS_CONNECTOR = {
  mode: 'reverse_tunnel',
  os_family: 'ubuntu',
  service_name: 'ktc-genieacs-tunnel',
  ssh_user: '',
  ssh_host: '',
  ssh_port: 22,
  identity_file: '/home/boncu/.ssh/ktc_vps_tunnel',
  reverse_remote_port: 27567,
  local_bind_host: '127.0.0.1',
  local_bind_port: 7557,
  genieacs_url_online: 'http://127.0.0.1:27567',
  notes: '',
};
const DEFAULT_FREERADIUS_CONNECTOR = {
  mode: 'ssh_tunnel',
  os_family: 'ubuntu',
  service_name: 'ktc-freeradius-db-tunnel',
  ssh_user: '',
  ssh_host: '',
  ssh_port: 22,
  identity_file: '/home/boncu/.ssh/ktc_vps_tunnel',
  local_port: 3307,
  remote_db_host: '127.0.0.1',
  remote_db_port: 3306,
  radius_host_online: '127.0.0.1',
  radius_port_online: 3307,
  radius_database: 'radius',
  radius_user: 'radius',
  radius_password: '',
  notes: '',
};
const DEFAULT_OPENNMS_SETTINGS = {
  enabled: false,
  base_url: '',
  username: '',
  password: '',
  verify_tls: false,
  timeout_seconds: 8,
};

const SETTINGS_TAB_META = {
  freeradius: {
    title: 'Integrasi FreeRADIUS',
    subtitle: 'Konfigurasi koneksi RADIUS, tunnel, dan sinkronisasi billing.',
  },
  genieacs: {
    title: 'Integrasi GenieACS',
    subtitle: 'Konfigurasi konektor ACS, tunnel, dan sinkronisasi status ONU.',
  },
  opennms: {
    title: 'Integrasi OpenNMS',
    subtitle: 'Konfigurasi koneksi OpenNMS untuk observabilitas dan monitoring NOC.',
  },
  whatsapp: {
    title: 'Integrasi WhatsApp',
    subtitle: 'Konfigurasi WhatsApp API untuk Live Chat, reminder, dan notifikasi pelanggan.',
  },
  reminder: {
    title: 'Operasional Auto Reminder',
    subtitle: 'Kelola scheduler pengingat jatuh tempo dan log pengiriman.',
  },
  payment: {
    title: 'Operasional Payment Gateway',
    subtitle: 'Kelola channel transfer dan provider gateway pembayaran.',
  },
};

const DEFAULT_BILLING_OVERDUE_POLICY = {
  auto_suspend_enabled: true,
  suspend_after_days: 3,
  auto_reactivate_on_clear: true,
};
const DEFAULT_SESSION_SECURITY = {
  single_session_enabled: true,
};
const ENABLE_FREERADIUS_MAPPING_UI = false;

const DEFAULT_PAYMENT_CHANNELS = {
  transfer_enabled: true,
  gateway_enabled: true,
  simulate_mode: true,
  default_provider: 'xendit',
  bank_accounts: [],
  providers: {
    duitku: { enabled: false, merchant_code: '', api_key: '', callback_url: '', return_url: '' },
    midtrans: { enabled: false, server_key: '', client_key: '', is_production: false, callback_url: '', return_url: '' },
    xendit: { enabled: false, secret_key: '', public_key: '', callback_token: '', callback_url: '', return_url: '' },
    ipaymu: { enabled: false, api_key: '', va: '', notify_url: '', return_url: '' },
  },
};

const normalizeFreeradiusMappings = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const rawItems = toArray(source.items);
  const normalizedItems = rawItems
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => ({
      id: item.id || `mapping-${index}-${Date.now()}`,
      package_key: item.package_key || '',
      package_name: item.package_name || '',
      rate_limit: item.rate_limit || '',
      enabled: item.enabled !== false,
    }));

  return {
    reply_attribute: source.reply_attribute || DEFAULT_FREERADIUS_MAPPINGS.reply_attribute,
    reply_operator: source.reply_operator || DEFAULT_FREERADIUS_MAPPINGS.reply_operator,
    prefer_package_speed: typeof source.prefer_package_speed === 'boolean' ? source.prefer_package_speed : DEFAULT_FREERADIUS_MAPPINGS.prefer_package_speed,
    items: normalizedItems.length > 0
      ? normalizedItems
      : DEFAULT_FREERADIUS_MAPPINGS.items.map((item) => ({ ...item })),
  };
};

const normalizeFreeradiusTunnel = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return {
    ssh_user: source.ssh_user || '',
    ssh_host: source.ssh_host || '',
    ssh_port: Number(source.ssh_port) || DEFAULT_FREERADIUS_TUNNEL.ssh_port,
    local_port: Number(source.local_port) || DEFAULT_FREERADIUS_TUNNEL.local_port,
    remote_db_host: source.remote_db_host || DEFAULT_FREERADIUS_TUNNEL.remote_db_host,
    remote_db_port: Number(source.remote_db_port) || DEFAULT_FREERADIUS_TUNNEL.remote_db_port,
    identity_file: source.identity_file || '',
    session_name: source.session_name || '',
  };
};

const normalizeGenieacsConnector = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const mode = String(source.mode || DEFAULT_GENIEACS_CONNECTOR.mode).trim().toLowerCase();
  const osFamily = String(source.os_family || DEFAULT_GENIEACS_CONNECTOR.os_family).trim().toLowerCase();
  return {
    mode: mode === 'direct_private' ? 'direct_private' : 'reverse_tunnel',
    os_family: osFamily === 'rhel' ? 'rhel' : 'ubuntu',
    service_name: String(source.service_name || DEFAULT_GENIEACS_CONNECTOR.service_name).trim() || DEFAULT_GENIEACS_CONNECTOR.service_name,
    ssh_user: String(source.ssh_user || DEFAULT_GENIEACS_CONNECTOR.ssh_user).trim(),
    ssh_host: String(source.ssh_host || DEFAULT_GENIEACS_CONNECTOR.ssh_host).trim(),
    ssh_port: Number(source.ssh_port) || DEFAULT_GENIEACS_CONNECTOR.ssh_port,
    identity_file: String(source.identity_file || DEFAULT_GENIEACS_CONNECTOR.identity_file).trim() || DEFAULT_GENIEACS_CONNECTOR.identity_file,
    reverse_remote_port: Number(source.reverse_remote_port) || DEFAULT_GENIEACS_CONNECTOR.reverse_remote_port,
    local_bind_host: String(source.local_bind_host || DEFAULT_GENIEACS_CONNECTOR.local_bind_host).trim() || DEFAULT_GENIEACS_CONNECTOR.local_bind_host,
    local_bind_port: Number(source.local_bind_port) || DEFAULT_GENIEACS_CONNECTOR.local_bind_port,
    genieacs_url_online: String(source.genieacs_url_online || DEFAULT_GENIEACS_CONNECTOR.genieacs_url_online).trim() || DEFAULT_GENIEACS_CONNECTOR.genieacs_url_online,
    notes: String(source.notes || '').trim(),
  };
};
const normalizeFreeradiusConnector = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const mode = String(source.mode || DEFAULT_FREERADIUS_CONNECTOR.mode).trim().toLowerCase();
  const osFamily = String(source.os_family || DEFAULT_FREERADIUS_CONNECTOR.os_family).trim().toLowerCase();
  return {
    mode: mode === 'direct' ? 'direct' : 'ssh_tunnel',
    os_family: osFamily === 'rhel' ? 'rhel' : 'ubuntu',
    service_name: String(source.service_name || DEFAULT_FREERADIUS_CONNECTOR.service_name).trim() || DEFAULT_FREERADIUS_CONNECTOR.service_name,
    ssh_user: String(source.ssh_user || DEFAULT_FREERADIUS_CONNECTOR.ssh_user).trim(),
    ssh_host: String(source.ssh_host || DEFAULT_FREERADIUS_CONNECTOR.ssh_host).trim(),
    ssh_port: Number(source.ssh_port) || DEFAULT_FREERADIUS_CONNECTOR.ssh_port,
    identity_file: String(source.identity_file || DEFAULT_FREERADIUS_CONNECTOR.identity_file).trim() || DEFAULT_FREERADIUS_CONNECTOR.identity_file,
    local_port: Number(source.local_port) || DEFAULT_FREERADIUS_CONNECTOR.local_port,
    remote_db_host: String(source.remote_db_host || DEFAULT_FREERADIUS_CONNECTOR.remote_db_host).trim() || DEFAULT_FREERADIUS_CONNECTOR.remote_db_host,
    remote_db_port: Number(source.remote_db_port) || DEFAULT_FREERADIUS_CONNECTOR.remote_db_port,
    radius_host_online: String(source.radius_host_online || DEFAULT_FREERADIUS_CONNECTOR.radius_host_online).trim() || DEFAULT_FREERADIUS_CONNECTOR.radius_host_online,
    radius_port_online: Number(source.radius_port_online) || DEFAULT_FREERADIUS_CONNECTOR.radius_port_online,
    radius_database: String(source.radius_database || DEFAULT_FREERADIUS_CONNECTOR.radius_database).trim() || DEFAULT_FREERADIUS_CONNECTOR.radius_database,
    radius_user: String(source.radius_user || DEFAULT_FREERADIUS_CONNECTOR.radius_user).trim() || DEFAULT_FREERADIUS_CONNECTOR.radius_user,
    radius_password: String(source.radius_password || DEFAULT_FREERADIUS_CONNECTOR.radius_password).trim(),
    notes: String(source.notes || '').trim(),
  };
};

const normalizeBillingOverduePolicy = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const parsedGraceDays = Number(source.suspend_after_days);
  return {
    auto_suspend_enabled: typeof source.auto_suspend_enabled === 'boolean'
      ? source.auto_suspend_enabled
      : DEFAULT_BILLING_OVERDUE_POLICY.auto_suspend_enabled,
    suspend_after_days: Number.isFinite(parsedGraceDays)
      ? Math.max(0, parsedGraceDays)
      : DEFAULT_BILLING_OVERDUE_POLICY.suspend_after_days,
    auto_reactivate_on_clear: typeof source.auto_reactivate_on_clear === 'boolean'
      ? source.auto_reactivate_on_clear
      : DEFAULT_BILLING_OVERDUE_POLICY.auto_reactivate_on_clear,
  };
};

const normalizePaymentChannels = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const providersSource = source.providers && typeof source.providers === 'object' ? source.providers : {};
  const bankAccounts = toArray(source.bank_accounts)
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: item.id || `bank-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      bank_name: item.bank_name || '',
      account_number: item.account_number || '',
      account_name: item.account_name || '',
      note: item.note || '',
      enabled: item.enabled !== false,
    }));

  const providers = Object.entries(DEFAULT_PAYMENT_CHANNELS.providers).reduce((acc, [key, defaults]) => {
    const current = providersSource[key] && typeof providersSource[key] === 'object' ? providersSource[key] : {};
    acc[key] = { ...defaults, ...current, enabled: Boolean(current.enabled ?? defaults.enabled) };
    return acc;
  }, {});

  return {
    transfer_enabled: Boolean(source.transfer_enabled ?? DEFAULT_PAYMENT_CHANNELS.transfer_enabled),
    gateway_enabled: Boolean(source.gateway_enabled ?? DEFAULT_PAYMENT_CHANNELS.gateway_enabled),
    simulate_mode: Boolean(source.simulate_mode ?? DEFAULT_PAYMENT_CHANNELS.simulate_mode),
    default_provider: source.default_provider || DEFAULT_PAYMENT_CHANNELS.default_provider,
    bank_accounts: bankAccounts,
    providers,
  };
};

const quotePowerShellValue = (value) => {
  const text = String(value ?? '');
  if (!text) return "''";
  if (/^[A-Za-z0-9_./:\\\-]+$/.test(text)) return text;
  return `'${text.replace(/'/g, "''")}'`;
};

const buildTunnelCommand = (settings) => {
  const safeSettings = settings && typeof settings === 'object' ? settings : {};
  const sessionName = String(safeSettings.session_name || '').trim();
  const args = [
    'powershell',
    '-ExecutionPolicy',
    'Bypass',
    '-File',
    '.\\scripts\\open-freeradius-tunnel.ps1',
  ];

  if (sessionName) {
    args.push('-SessionName', quotePowerShellValue(sessionName));
  } else {
    args.push(
      '-SshUser',
      quotePowerShellValue(safeSettings.ssh_user || 'USER_VPS'),
      '-SshHost',
      quotePowerShellValue(safeSettings.ssh_host || 'IP_VPS'),
      '-SshPort',
      String(Number(safeSettings.ssh_port) || 22),
    );
    if (safeSettings.identity_file) {
      args.push('-IdentityFile', quotePowerShellValue(safeSettings.identity_file));
    }
  }

  args.push(
    '-LocalPort',
    String(Number(safeSettings.local_port) || 3307),
    '-RemoteDbHost',
    quotePowerShellValue(safeSettings.remote_db_host || '127.0.0.1'),
    '-RemoteDbPort',
    String(Number(safeSettings.remote_db_port) || 3306),
  );

  return args.join(' ');
};

const normalizeOpennmsSettings = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const parsedTimeout = Number(source.timeout_seconds);
  return {
    enabled: Boolean(source.enabled ?? DEFAULT_OPENNMS_SETTINGS.enabled),
    base_url: String(source.base_url || DEFAULT_OPENNMS_SETTINGS.base_url).trim(),
    username: String(source.username || DEFAULT_OPENNMS_SETTINGS.username).trim(),
    password: String(source.password || DEFAULT_OPENNMS_SETTINGS.password),
    verify_tls: Boolean(source.verify_tls ?? DEFAULT_OPENNMS_SETTINGS.verify_tls),
    timeout_seconds: Number.isFinite(parsedTimeout) ? Math.max(3, parsedTimeout) : DEFAULT_OPENNMS_SETTINGS.timeout_seconds,
  };
};

const buildGenieacsConnectorScript = (settings) => {
  const cfg = normalizeGenieacsConnector(settings);
  const installer = cfg.os_family === 'rhel'
    ? 'sudo dnf -y install autossh || sudo yum -y install autossh'
    : 'sudo apt update && sudo apt -y install autossh';

  if (cfg.mode === 'direct_private') {
    return [
      '# KTC GenieACS Connector (Direct Private URL)',
      '# Gunakan mode ini jika VPS bisa langsung akses IP LAN GenieACS (site-to-site VPN sudah aktif).',
      '',
      `# Set backend online URL ke GenieACS private`,
      `# Contoh .env backend online:`,
      `GENIEACS_URL=${cfg.genieacs_url_online}`,
      'GENIEACS_USERNAME=admin',
      'GENIEACS_PASSWORD=<isi-password>',
      '',
      '# Uji koneksi dari VPS',
      `curl -sS --connect-timeout 5 "${cfg.genieacs_url_online}/devices?limit=1"`,
      '',
      '# Lalu restart backend aplikasi',
      'sudo systemctl restart ktc-web-backend',
    ].join('\n');
  }

  const sshPort = Number(cfg.ssh_port) || 22;
  const remotePort = Number(cfg.reverse_remote_port) || 27567;
  const localPort = Number(cfg.local_bind_port) || 7557;
  const localHost = cfg.local_bind_host || '127.0.0.1';
  const serviceName = cfg.service_name || 'ktc-genieacs-tunnel';
  const sshUserHost = `${cfg.ssh_user || 'boncux'}@${cfg.ssh_host || '103.197.190.29'}`;
  const identityFile = cfg.identity_file || '/home/boncu/.ssh/ktc_vps_tunnel';

  return [
    '# KTC GenieACS Connector (Reverse Tunnel)',
    '# Jalankan di server GenieACS (lokal), bukan di VPS.',
    '',
    '# 1) Install autossh',
    installer,
    '',
    '# 2) Buat service tunnel',
    `sudo tee /etc/systemd/system/${serviceName}.service >/dev/null <<'EOF'`,
    '[Unit]',
    'Description=Reverse Tunnel GenieACS to VPS',
    'After=network-online.target',
    'Wants=network-online.target',
    '',
    '[Service]',
    'Type=simple',
    `ExecStart=/usr/bin/autossh -M 0 -N -o ServerAliveInterval=30 -o ServerAliveCountMax=3 -o ExitOnForwardFailure=yes -i ${identityFile} -p ${sshPort} -R ${remotePort}:${localHost}:${localPort} ${sshUserHost}`,
    'Restart=always',
    'RestartSec=5',
    '',
    '[Install]',
    'WantedBy=multi-user.target',
    'EOF',
    '',
    '# 3) Enable + start service',
    `sudo systemctl daemon-reload && sudo systemctl enable --now ${serviceName}`,
    `sudo systemctl --no-pager -l status ${serviceName}`,
    '',
    '# 4) Set backend online (VPS) ke endpoint tunnel',
    `# .env backend online -> GENIEACS_URL=http://127.0.0.1:${remotePort}`,
    '# lalu restart backend online',
    'sudo systemctl restart ktc-web-backend',
    '',
    '# 5) Verifikasi dari VPS',
    `curl -sS --connect-timeout 5 "http://127.0.0.1:${remotePort}/devices?limit=1"`,
  ].join('\n');
};

const buildFreeradiusConnectorScript = (settings) => {
  const cfg = normalizeFreeradiusConnector(settings);
  if (cfg.mode === 'direct') {
    return [
      '# KTC FreeRADIUS Connector (Direct DB)',
      '# Gunakan jika backend online bisa akses DB FreeRADIUS langsung (private network/VPN).',
      '',
      '# .env backend online:',
      `RADIUS_DB_HOST=${cfg.radius_host_online}`,
      `RADIUS_DB_PORT=${cfg.radius_port_online}`,
      `RADIUS_DB_NAME=${cfg.radius_database}`,
      `RADIUS_DB_USER=${cfg.radius_user}`,
      `RADIUS_DB_PASSWORD=<isi-password>`,
      '',
      '# Uji port dari VPS:',
      `nc -zv ${cfg.radius_host_online} ${cfg.radius_port_online}`,
      '',
      '# Restart backend:',
      'sudo systemctl restart ktc-web-backend',
    ].join('\n');
  }

  const installer = cfg.os_family === 'rhel'
    ? 'sudo dnf -y install autossh || sudo yum -y install autossh'
    : 'sudo apt update && sudo apt -y install autossh';
  const sshPort = Number(cfg.ssh_port) || 22;
  const localPort = Number(cfg.local_port) || 3307;
  const remotePort = Number(cfg.remote_db_port) || 3306;
  const serviceName = cfg.service_name || 'ktc-freeradius-db-tunnel';
  const sshUserHost = `${cfg.ssh_user || 'boncux'}@${cfg.ssh_host || '103.197.190.29'}`;
  const identityFile = cfg.identity_file || '/home/boncu/.ssh/ktc_vps_tunnel';
  const remoteDbHost = cfg.remote_db_host || '127.0.0.1';

  return [
    '# KTC FreeRADIUS Connector (SSH Tunnel)',
    '# Jalankan di server backend online (VPS aplikasi) jika DB FreeRADIUS ada di server berbeda.',
    '',
    '# 1) Install autossh',
    installer,
    '',
    '# 2) Buat service tunnel',
    `sudo tee /etc/systemd/system/${serviceName}.service >/dev/null <<'EOF'`,
    '[Unit]',
    'Description=KTC FreeRADIUS DB Tunnel',
    'After=network-online.target',
    'Wants=network-online.target',
    '',
    '[Service]',
    'Type=simple',
    `ExecStart=/usr/bin/autossh -M 0 -N -o ServerAliveInterval=30 -o ServerAliveCountMax=3 -o ExitOnForwardFailure=yes -i ${identityFile} -p ${sshPort} -L ${localPort}:${remoteDbHost}:${remotePort} ${sshUserHost}`,
    'Restart=always',
    'RestartSec=5',
    '',
    '[Install]',
    'WantedBy=multi-user.target',
    'EOF',
    '',
    '# 3) Enable + start service',
    `sudo systemctl daemon-reload && sudo systemctl enable --now ${serviceName}`,
    `sudo systemctl --no-pager -l status ${serviceName}`,
    '',
    '# 4) Set .env backend online ke localhost tunnel',
    `RADIUS_DB_HOST=127.0.0.1`,
    `RADIUS_DB_PORT=${localPort}`,
    `RADIUS_DB_NAME=${cfg.radius_database}`,
    `RADIUS_DB_USER=${cfg.radius_user}`,
    'RADIUS_DB_PASSWORD=<isi-password>',
    '',
    '# 5) Verifikasi dan restart backend',
    `nc -zv 127.0.0.1 ${localPort}`,
    'sudo systemctl restart ktc-web-backend',
  ].join('\n');
};

const buildGenieacsEnvSnippet = (settings) => {
  const cfg = normalizeGenieacsConnector(settings);
  const url = cfg.mode === 'reverse_tunnel'
    ? `http://127.0.0.1:${Number(cfg.reverse_remote_port) || 27567}`
    : (cfg.genieacs_url_online || 'http://10.10.80.8:7557');
  return [
    `GENIEACS_URL=${url}`,
    'GENIEACS_USERNAME=admin',
    'GENIEACS_PASSWORD=change-this',
    'GENIEACS_ONLINE_WINDOW_MINUTES=15',
    'GENIEACS_TIMEOUT_SECONDS=8',
    'GENIEACS_STREAM_INTERVAL_SECONDS=5',
    'GENIEACS_HEALTH_GRACE_SECONDS=120',
    'GENIEACS_HEALTH_DOWN_AFTER_FAILURES=5',
  ].join('\n');
};

const buildFreeradiusEnvSnippet = (settings) => {
  const cfg = normalizeFreeradiusConnector(settings);
  const host = cfg.mode === 'ssh_tunnel' ? '127.0.0.1' : (cfg.radius_host_online || '127.0.0.1');
  const port = cfg.mode === 'ssh_tunnel'
    ? (Number(cfg.local_port) || 3307)
    : (Number(cfg.radius_port_online) || 3306);
  return [
    '# Catatan: app saat ini memakai Settings > FreeRADIUS (DB Mongo),',
    '# snippet ini untuk referensi deploy/.env atau service eksternal.',
    `RADIUS_DB_HOST=${host}`,
    `RADIUS_DB_PORT=${port}`,
    `RADIUS_DB_NAME=${cfg.radius_database || 'radius'}`,
    `RADIUS_DB_USER=${cfg.radius_user || 'radius'}`,
    'RADIUS_DB_PASSWORD=change-this',
  ].join('\n');
};

const AdminSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('freeradius');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [freeradiusStatus, setFreeradiusStatus] = useState(null);
  const [loadingFreeradiusStatus, setLoadingFreeradiusStatus] = useState(false);
  const [syncingFreeRadius, setSyncingFreeRadius] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [genieacsHealth, setGenieacsHealth] = useState(null);
  const [loadingGenieacsHealth, setLoadingGenieacsHealth] = useState(false);
  const [opennmsSettings, setOpennmsSettings] = useState(() => normalizeOpennmsSettings(DEFAULT_OPENNMS_SETTINGS));
  const [opennmsHealth, setOpennmsHealth] = useState(null);
  const [loadingOpennmsHealth, setLoadingOpennmsHealth] = useState(false);
  const [savingOpennmsSettings, setSavingOpennmsSettings] = useState(false);
  const [reconcilingDb, setReconcilingDb] = useState(false);
  const [reconcileDbResult, setReconcileDbResult] = useState(null);
  const [freeradiusMappings, setFreeradiusMappings] = useState(() => normalizeFreeradiusMappings(DEFAULT_FREERADIUS_MAPPINGS));
  const [freeradiusTunnel, setFreeradiusTunnel] = useState(() => normalizeFreeradiusTunnel(DEFAULT_FREERADIUS_TUNNEL));
  const [freeradiusConnector, setFreeradiusConnector] = useState(() => normalizeFreeradiusConnector(DEFAULT_FREERADIUS_CONNECTOR));
  const [genieacsConnector, setGenieacsConnector] = useState(() => normalizeGenieacsConnector(DEFAULT_GENIEACS_CONNECTOR));
  const [billingOverduePolicy, setBillingOverduePolicy] = useState(() => normalizeBillingOverduePolicy(DEFAULT_BILLING_OVERDUE_POLICY));
  const [sessionSecurity, setSessionSecurity] = useState(() => ({ ...DEFAULT_SESSION_SECURITY }));
  const [paymentChannels, setPaymentChannels] = useState(() => normalizePaymentChannels(DEFAULT_PAYMENT_CHANNELS));
  const [savingMappings, setSavingMappings] = useState(false);
  const [savingTunnel, setSavingTunnel] = useState(false);
  const [savingFreeradiusConnector, setSavingFreeradiusConnector] = useState(false);
  const [savingGenieacsConnector, setSavingGenieacsConnector] = useState(false);
  const [applyingEnvTarget, setApplyingEnvTarget] = useState('');
  const [savingBillingPolicy, setSavingBillingPolicy] = useState(false);
  const [savingSessionSecurity, setSavingSessionSecurity] = useState(false);
  const [savingPaymentChannels, setSavingPaymentChannels] = useState(false);
  
  // FreeRADIUS Settings
  const [freeradiusSettings, setFreeradiusSettings] = useState({
    host: 'localhost',
    port: 3306,
    user: 'radius',
    password: '',
    database: 'radius',
    auto_sync: false
  });
  
  // Reminder Settings
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    days_before_due: 3,
    whatsapp_enabled: true,
    admin_phone: ''
  });
  const [whatsappMetaSettings, setWhatsappMetaSettings] = useState({
    enabled: false,
    api_version: 'v21.0',
    phone_number_id: '',
    access_token: '',
    reminder_enabled: true,
    thank_you_enabled: true,
    template_overdue: '[KTC] Tagihan internet Anda jatuh tempo pada {due_date}. Invoice: {invoice}. Total: Rp {amount}.',
    template_thank_you: '[KTC] Terima kasih, pembayaran invoice {invoice} sebesar Rp {amount} sudah kami terima.',
  });
  const [savingWhatsappMeta, setSavingWhatsappMeta] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [enqueueingReminder, setEnqueueingReminder] = useState(false);
  const [queueProcessResult, setQueueProcessResult] = useState(null);

  // Reminders List
  const [reminders, setReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);

  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState({ running: false, jobs: [] });
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [backupLoading, setBackupLoading] = useState(false);
  const [backupImportLoading, setBackupImportLoading] = useState(false);
  const [backupImportFile, setBackupImportFile] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingBackupStatus, setLoadingBackupStatus] = useState(false);
  const backupInputRef = useRef(null);
  const nextSchedulerJob = schedulerStatus.jobs.find((job) => job?.next_run);
  const backupAgeHours = Number(backupStatus?.age_hours);
  const backupIsStale = Number.isFinite(backupAgeHours) && backupAgeHours > 24;
  const backupLastTime = backupStatus?.latest_backup_at ? formatDateTime(backupStatus.latest_backup_at) : '-';
  const tunnelCommand = buildTunnelCommand(freeradiusTunnel);
  const freeradiusConnectorScript = buildFreeradiusConnectorScript(freeradiusConnector);
  const freeradiusEnvSnippet = buildFreeradiusEnvSnippet(freeradiusConnector);
  const genieacsConnectorScript = buildGenieacsConnectorScript(genieacsConnector);
  const genieacsEnvSnippet = buildGenieacsEnvSnippet(genieacsConnector);


  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const headers = getHeaders();
      const [frRes, rmRes, mappingRes, tunnelRes, freeradiusConnectorRes, genieacsConnectorRes, opennmsRes, billingPolicyRes, sessionSecurityRes, waMetaRes, paymentChannelsRes] = await Promise.allSettled([
        axios.get(`${API}/settings/freeradius`, { headers }),
        axios.get(`${API}/settings/reminder`, { headers }),
        axios.get(`${API}/settings/freeradius/mappings`, { headers }),
        axios.get(`${API}/settings/freeradius/tunnel`, { headers }),
        axios.get(`${API}/settings/freeradius/connector`, { headers }),
        axios.get(`${API}/settings/genieacs/connector`, { headers }),
        axios.get(`${API}/settings/opennms`, { headers }),
        axios.get(`${API}/settings/billing-overdue-policy`, { headers }),
        axios.get(`${API}/settings/session-security`, { headers }),
        axios.get(`${API}/settings/whatsapp-meta`, { headers }),
        axios.get(`${API}/settings/payment-channels`, { headers }),
      ]);

      if (frRes.status === 'fulfilled' && frRes.value.data && typeof frRes.value.data === 'object') {
        setFreeradiusSettings((prev) => ({ ...prev, ...frRes.value.data }));
      }

      if (rmRes.status === 'fulfilled' && rmRes.value.data && typeof rmRes.value.data === 'object') {
        setReminderSettings((prev) => ({ ...prev, ...rmRes.value.data }));
      }

      if (mappingRes.status === 'fulfilled') {
        setFreeradiusMappings(normalizeFreeradiusMappings(mappingRes.value.data));
      }

      if (tunnelRes.status === 'fulfilled') {
        setFreeradiusTunnel(normalizeFreeradiusTunnel(tunnelRes.value.data));
      }

      if (freeradiusConnectorRes.status === 'fulfilled') {
        setFreeradiusConnector(normalizeFreeradiusConnector(freeradiusConnectorRes.value.data));
      }

      if (genieacsConnectorRes.status === 'fulfilled') {
        setGenieacsConnector(normalizeGenieacsConnector(genieacsConnectorRes.value.data));
      }

      if (opennmsRes.status === 'fulfilled') {
        setOpennmsSettings(normalizeOpennmsSettings(opennmsRes.value.data));
      }

      if (billingPolicyRes.status === 'fulfilled') {
        setBillingOverduePolicy(normalizeBillingOverduePolicy(billingPolicyRes.value.data));
      }

      if (sessionSecurityRes.status === 'fulfilled' && sessionSecurityRes.value.data && typeof sessionSecurityRes.value.data === 'object') {
        setSessionSecurity((prev) => ({
          ...prev,
          single_session_enabled: Boolean(sessionSecurityRes.value.data.single_session_enabled ?? prev.single_session_enabled),
        }));
      }

      if (waMetaRes.status === 'fulfilled' && waMetaRes.value.data && typeof waMetaRes.value.data === 'object') {
        setWhatsappMetaSettings((prev) => ({ ...prev, ...waMetaRes.value.data }));
      }

      if (paymentChannelsRes.status === 'fulfilled') {
        setPaymentChannels(normalizePaymentChannels(paymentChannelsRes.value.data));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [getHeaders]);

  const fetchFreeradiusStatus = useCallback(async () => {
    setLoadingFreeradiusStatus(true);
    try {
      const response = await axios.get(`${API}/freeradius/status`, { headers: getHeaders() });
      setFreeradiusStatus(response.data && typeof response.data === 'object' ? response.data : null);
    } catch (error) {
      setFreeradiusStatus(null);
    } finally {
      setLoadingFreeradiusStatus(false);
    }
  }, [getHeaders]);

  const fetchGenieacsHealth = useCallback(async () => {
    setLoadingGenieacsHealth(true);
    try {
      const response = await axios.get(`${API}/genieacs/health`, { headers: getHeaders() });
      setGenieacsHealth(response.data && typeof response.data === 'object' ? response.data : null);
    } catch (error) {
      setGenieacsHealth(null);
      toast.error(error.response?.data?.detail || 'Gagal cek koneksi GenieACS');
    } finally {
      setLoadingGenieacsHealth(false);
    }
  }, [getHeaders]);

  const fetchOpennmsHealth = useCallback(async () => {
    setLoadingOpennmsHealth(true);
    try {
      const response = await axios.get(`${API}/opennms/health`, { headers: getHeaders() });
      setOpennmsHealth(response.data && typeof response.data === 'object' ? response.data : null);
    } catch (error) {
      setOpennmsHealth(null);
      toast.error(error.response?.data?.detail || 'Gagal cek koneksi OpenNMS');
    } finally {
      setLoadingOpennmsHealth(false);
    }
  }, [getHeaders]);

  const fetchBackupStatus = useCallback(async () => {
    setLoadingBackupStatus(true);
    try {
      const response = await axios.get(`${API}/system/backup/status`, { headers: getHeaders() });
      setBackupStatus(response.data && typeof response.data === 'object' ? response.data : null);
    } catch (error) {
      setBackupStatus(null);
    } finally {
      setLoadingBackupStatus(false);
    }
  }, [getHeaders]);

  const handleReconcileDb = async () => {
    setReconcilingDb(true);
    setReconcileDbResult(null);
    try {
      const response = await axios.post(`${API}/admin/db/reconcile`, {}, { headers: getHeaders() });
      setReconcileDbResult(response.data && typeof response.data === 'object' ? response.data : null);
      toast.success(response.data?.message || 'Rekonsiliasi database selesai');
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal rekonsiliasi database';
      setReconcileDbResult({ message });
      toast.error(message);
    } finally {
      setReconcilingDb(false);
    }
  };

  const handleSaveFreeradius = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/freeradius`, freeradiusSettings, { headers: getHeaders() });
      toast.success('Pengaturan FreeRADIUS berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/freeradius/test`, freeradiusSettings, { headers: getHeaders() });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal test koneksi';
      setTestResult({ success: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const handleSyncFreeRadius = async () => {
    setSyncingFreeRadius(true);
    setSyncResult(null);
    try {
      let response;
      try {
        response = await axios.post(`${API}/freeradius/sync-with-log`, {}, { headers: getHeaders() });
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        response = await axios.post(`${API}/freeradius/sync`, freeradiusSettings, { headers: getHeaders() });
      }
      setSyncResult(response.data && typeof response.data === 'object' ? response.data : null);
      toast.success(response.data?.message || 'Sync FreeRADIUS berhasil');
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal sync ke FreeRADIUS';
      setSyncResult({ message, errors: [{ error: message }] });
      toast.error(message);
    } finally {
      setSyncingFreeRadius(false);
    }
  };

  const handleAddMappingRow = () => {
    setFreeradiusMappings((prev) => ({
      ...prev,
      items: [
        ...toArray(prev.items),
        { id: `mapping-${Date.now()}`, package_key: '', package_name: '', rate_limit: '', enabled: true },
      ],
    }));
  };

  const handleUpdateMappingRow = (mappingId, field, value) => {
    setFreeradiusMappings((prev) => ({
      ...prev,
      items: toArray(prev.items).map((item) => (item.id === mappingId ? { ...item, [field]: value } : item)),
    }));
  };

  const handleRemoveMappingRow = (mappingId) => {
    setFreeradiusMappings((prev) => ({
      ...prev,
      items: toArray(prev.items).filter((item) => item.id !== mappingId),
    }));
  };

  const handleSaveFreeradiusMappings = async () => {
    setSavingMappings(true);
    try {
      const payload = {
        reply_attribute: (freeradiusMappings.reply_attribute || 'Mikrotik-Rate-Limit').trim() || 'Mikrotik-Rate-Limit',
        reply_operator: (freeradiusMappings.reply_operator || ':=').trim() || ':=',
        prefer_package_speed: Boolean(freeradiusMappings.prefer_package_speed),
        items: toArray(freeradiusMappings.items)
          .map((item) => ({
            id: item.id || `mapping-${Date.now()}`,
            package_key: (item.package_key || '').trim(),
            package_name: (item.package_name || '').trim(),
            rate_limit: (item.rate_limit || '').trim(),
            enabled: item.enabled !== false,
          }))
          .filter((item) => item.rate_limit && (item.package_key || item.package_name)),
      };

      const response = await axios.post(`${API}/settings/freeradius/mappings`, payload, { headers: getHeaders() });
      setFreeradiusMappings(normalizeFreeradiusMappings(response.data?.settings || payload));
      toast.success(response.data?.message || 'Mapping paket FreeRADIUS berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan mapping paket FreeRADIUS');
    } finally {
      setSavingMappings(false);
    }
  };

  const handleSaveFreeradiusTunnel = async () => {
    setSavingTunnel(true);
    try {
      const payload = normalizeFreeradiusTunnel(freeradiusTunnel);
      const response = await axios.post(`${API}/settings/freeradius/tunnel`, payload, { headers: getHeaders() });
      setFreeradiusTunnel(normalizeFreeradiusTunnel(response.data?.settings || payload));
      toast.success(response.data?.message || 'Generator SSH tunnel berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan generator SSH tunnel');
    } finally {
      setSavingTunnel(false);
    }
  };

  const handleSaveGenieacsConnector = async () => {
    setSavingGenieacsConnector(true);
    try {
      const payload = normalizeGenieacsConnector(genieacsConnector);
      const response = await axios.post(`${API}/settings/genieacs/connector`, payload, { headers: getHeaders() });
      setGenieacsConnector(normalizeGenieacsConnector(response.data?.settings || payload));
      toast.success(response.data?.message || 'Generator konektor GenieACS berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan generator konektor GenieACS');
    } finally {
      setSavingGenieacsConnector(false);
    }
  };

  const handleSaveOpennmsSettings = async () => {
    setSavingOpennmsSettings(true);
    try {
      const payload = normalizeOpennmsSettings(opennmsSettings);
      const response = await axios.post(`${API}/settings/opennms`, payload, { headers: getHeaders() });
      setOpennmsSettings(normalizeOpennmsSettings(response.data?.settings || payload));
      toast.success(response.data?.message || 'Pengaturan OpenNMS berhasil disimpan');
      fetchOpennmsHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan OpenNMS');
    } finally {
      setSavingOpennmsSettings(false);
    }
  };

  const handleSaveFreeradiusConnector = async () => {
    setSavingFreeradiusConnector(true);
    try {
      const payload = normalizeFreeradiusConnector(freeradiusConnector);
      const response = await axios.post(`${API}/settings/freeradius/connector`, payload, { headers: getHeaders() });
      setFreeradiusConnector(normalizeFreeradiusConnector(response.data?.settings || payload));
      toast.success(response.data?.message || 'Generator konektor FreeRADIUS berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan generator konektor FreeRADIUS');
    } finally {
      setSavingFreeradiusConnector(false);
    }
  };

  const handleSaveBillingOverduePolicy = async () => {
    setSavingBillingPolicy(true);
    try {
      const payload = normalizeBillingOverduePolicy(billingOverduePolicy);
      const response = await axios.post(`${API}/settings/billing-overdue-policy`, payload, { headers: getHeaders() });
      setBillingOverduePolicy(normalizeBillingOverduePolicy(response.data?.settings || payload));
      toast.success(response.data?.message || 'Kebijakan suspend overdue berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan kebijakan suspend overdue');
    } finally {
      setSavingBillingPolicy(false);
    }
  };

  const handleSaveSessionSecurity = async () => {
    setSavingSessionSecurity(true);
    try {
      const payload = { single_session_enabled: Boolean(sessionSecurity.single_session_enabled) };
      const response = await axios.post(`${API}/settings/session-security`, payload, { headers: getHeaders() });
      const nextValue = response?.data?.settings && typeof response.data.settings === 'object'
        ? response.data.settings
        : payload;
      setSessionSecurity({
        single_session_enabled: Boolean(nextValue.single_session_enabled),
      });
      toast.success(response?.data?.message || 'Pengaturan keamanan sesi login berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan keamanan sesi login');
    } finally {
      setSavingSessionSecurity(false);
    }
  };
  const handleAddBankAccount = () => {
    setPaymentChannels((prev) => ({
      ...prev,
      bank_accounts: [
        ...(toArray(prev.bank_accounts)),
        {
          id: `bank-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          bank_name: '',
          account_number: '',
          account_name: '',
          note: '',
          enabled: true,
        },
      ],
    }));
  };

  const handleUpdateBankAccount = (id, field, value) => {
    setPaymentChannels((prev) => ({
      ...prev,
      bank_accounts: toArray(prev.bank_accounts).map((item) => (
        item.id === id ? { ...item, [field]: value } : item
      )),
    }));
  };

  const handleRemoveBankAccount = (id) => {
    setPaymentChannels((prev) => ({
      ...prev,
      bank_accounts: toArray(prev.bank_accounts).filter((item) => item.id !== id),
    }));
  };

  const handleUpdateGatewayProvider = (providerKey, field, value) => {
    setPaymentChannels((prev) => ({
      ...prev,
      providers: {
        ...(prev.providers || {}),
        [providerKey]: {
          ...((prev.providers || {})[providerKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleSavePaymentChannels = async () => {
    setSavingPaymentChannels(true);
    try {
      const payload = normalizePaymentChannels(paymentChannels);
      const response = await axios.post(`${API}/settings/payment-channels`, payload, { headers: getHeaders() });
      setPaymentChannels(normalizePaymentChannels(response.data?.settings || payload));
      toast.success(response.data?.message || 'Pengaturan payment channels berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan payment channels');
    } finally {
      setSavingPaymentChannels(false);
    }
  };

  const handleCopyTunnelCommand = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(tunnelCommand);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = tunnelCommand;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Command SSH tunnel berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin command SSH tunnel');
    }
  };

  const handleCopyGenieacsScript = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(genieacsConnectorScript);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = genieacsConnectorScript;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Script konektor GenieACS berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin script konektor GenieACS');
    }
  };

  const handleApplyGenieacsUrl = () => {
    const targetPort = Number(genieacsConnector.reverse_remote_port) || 27567;
    setGenieacsConnector((prev) => ({
      ...prev,
      genieacs_url_online: prev.mode === 'reverse_tunnel' ? `http://127.0.0.1:${targetPort}` : prev.genieacs_url_online,
    }));
    toast.success('URL GenieACS online diisi otomatis sesuai mode');
  };

  const handleCopyFreeradiusScript = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(freeradiusConnectorScript);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = freeradiusConnectorScript;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Script konektor FreeRADIUS berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin script konektor FreeRADIUS');
    }
  };

  const handleApplyFreeradiusConnectorToConnection = () => {
    const mode = String(freeradiusConnector.mode || 'ssh_tunnel');
    const host = mode === 'ssh_tunnel'
      ? '127.0.0.1'
      : (freeradiusConnector.radius_host_online || freeradiusSettings.host || '127.0.0.1');
    const port = mode === 'ssh_tunnel'
      ? (Number(freeradiusConnector.local_port) || 3307)
      : (Number(freeradiusConnector.radius_port_online) || 3306);

    setFreeradiusSettings((prev) => ({
      ...prev,
      host,
      port,
      database: String(freeradiusConnector.radius_database || prev.database || 'radius'),
      user: String(freeradiusConnector.radius_user || prev.user || 'radius'),
      password: String(freeradiusConnector.radius_password || prev.password || ''),
    }));
    toast.success('Koneksi MySQL FreeRADIUS diisi otomatis dari generator');
  };

  const handleCopyFreeradiusEnvSnippet = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(freeradiusEnvSnippet);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = freeradiusEnvSnippet;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('.env snippet FreeRADIUS berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin .env snippet FreeRADIUS');
    }
  };

  const handleCopyGenieacsEnvSnippet = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(genieacsEnvSnippet);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = genieacsEnvSnippet;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('.env snippet GenieACS berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin .env snippet GenieACS');
    }
  };

  const handleApplyEnvOnServer = async (target) => {
    const safeTarget = String(target || '').trim().toLowerCase();
    if (!['freeradius', 'genieacs', 'all'].includes(safeTarget)) {
      toast.error('Target env tidak valid');
      return;
    }
    setApplyingEnvTarget(safeTarget);
    try {
      const response = await axios.post(
        `${API}/settings/env/apply`,
        { target: safeTarget, restart_backend: true },
        { headers: getHeaders() },
      );
      const data = response.data && typeof response.data === 'object' ? response.data : {};
      const restart = data.restart && typeof data.restart === 'object' ? data.restart : {};
      if (restart.ok) {
        toast.success(`${data.message || 'Berhasil apply .env'} + backend restart OK`);
      } else {
        toast.success(data.message || 'Berhasil apply .env');
        if (restart.requested && restart.error) {
          toast.warning(`Restart backend belum otomatis: ${restart.error}`);
        }
      }
      fetchFreeradiusStatus();
      fetchGenieacsHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal apply snippet ke .env VPS');
    } finally {
      setApplyingEnvTarget('');
    }
  };

  const handleApplyTunnelToConnection = () => {
    setFreeradiusSettings((prev) => ({
      ...prev,
      host: '127.0.0.1',
      port: Number(freeradiusTunnel.local_port) || 3307,
    }));
    toast.success('Host dan port koneksi diisi dari SSH tunnel');
  };

  

  const handleSaveReminder = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/reminder`, reminderSettings, { headers: getHeaders() });
      toast.success('Pengaturan reminder berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };
  const handleSaveWhatsappMeta = async () => {
    setSavingWhatsappMeta(true);
    try {
      const response = await axios.post(`${API}/settings/whatsapp-meta`, whatsappMetaSettings, { headers: getHeaders() });
      const nextSettings = response.data?.settings && typeof response.data.settings === 'object'
        ? response.data.settings
        : whatsappMetaSettings;
      setWhatsappMetaSettings((prev) => ({ ...prev, ...nextSettings }));
      toast.success(response.data?.message || 'Pengaturan WhatsApp Meta berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan WhatsApp Meta');
    } finally {
      setSavingWhatsappMeta(false);
    }
  };

  const handleProcessWhatsappQueue = async () => {
    setProcessingQueue(true);
    try {
      const response = await axios.post(`${API}/notifications/queue/process`, {}, { headers: getHeaders() });
      setQueueProcessResult(response.data);
      toast.success(response.data?.message || 'Queue WhatsApp diproses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses queue WhatsApp');
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleEnqueueDueReminders = async () => {
    setEnqueueingReminder(true);
    try {
      const daysBefore = Number(reminderSettings.days_before_due) || 3;
      const response = await axios.post(`${API}/billings/reminders/enqueue`, {}, {
        headers: getHeaders(),
        params: { days_before: daysBefore },
      });
      toast.success(`${response.data?.queued || 0} reminder dimasukkan ke antrean`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengantrikan reminder');
    } finally {
      setEnqueueingReminder(false);
    }
  };

  const handleExportBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup/export`, {
        headers: getHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ktc-backup-${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup sistem berhasil diunduh');
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengunduh backup sistem');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleImportBackup = async () => {
    if (!backupImportFile) {
      toast.error('Pilih file backup JSON terlebih dahulu');
      return;
    }

    setBackupImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', backupImportFile);
      const response = await axios.post(`${API}/admin/backup/import`, formData, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      const processedItems = Number(response.data?.processed_items || 0);
      const updatedItems = Number(response.data?.updated_items || 0);
      toast.success(`Import backup selesai. Diproses ${processedItems} item, update ${updatedItems} item.`);
      setBackupImportFile(null);
      if (backupInputRef.current) backupInputRef.current.value = '';
      fetchSettings();
      fetchSchedulerStatus();
      fetchReminderLogs();
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengimpor backup sistem');
    } finally {
      setBackupImportLoading(false);
    }
  };

  const handleCheckReminders = async () => {
    setLoadingReminders(true);
    try {
      const res = await axios.post(`${API}/billings/check-reminders`, {}, { headers: getHeaders() });
      const nextReminders = toArray(res.data?.reminders).filter((item) => item && typeof item === 'object');
      setReminders(nextReminders);
      toast.success(`Ditemukan ${Number(res.data?.total || nextReminders.length)} tagihan perlu diingatkan`);
    } catch (error) {
      toast.error('Gagal mengambil data reminder');
      setReminders([]);
    } finally {
      setLoadingReminders(false);
    }
  };

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scheduler/status`, { headers: getHeaders() });
      setSchedulerStatus(normalizeSchedulerStatus(res.data));
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      setSchedulerStatus(normalizeSchedulerStatus(null));
    }
  }, [getHeaders]);
  useEffect(() => {
    const tab = new URLSearchParams(location.search || '').get('tab');
    if (!tab) return;
    if (['freeradius', 'genieacs', 'opennms', 'whatsapp', 'reminder', 'security', 'payment'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Invalid stored user payload:', error);
        localStorage.removeItem('ktc_user');
      }
    }
    fetchSettings();
    fetchSchedulerStatus();
    fetchFreeradiusStatus();
    fetchGenieacsHealth();
    fetchOpennmsHealth();
    fetchBackupStatus();
  }, [fetchSettings, fetchSchedulerStatus, fetchFreeradiusStatus, fetchGenieacsHealth, fetchOpennmsHealth, fetchBackupStatus]);

  const handleStartScheduler = async () => {
    setSchedulerLoading(true);
    try {
      const res = await axios.post(`${API}/scheduler/start`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      fetchSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memulai scheduler');
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleStopScheduler = async () => {
    setSchedulerLoading(true);
    try {
      const res = await axios.post(`${API}/scheduler/stop`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      fetchSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghentikan scheduler');
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleRunNow = async () => {
    setSchedulerLoading(true);
    try {
      const res = await axios.post(`${API}/scheduler/run-now`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      fetchReminderLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menjalankan reminder check');
    } finally {
      setSchedulerLoading(false);
    }
  };

  const fetchReminderLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/reminder-logs?limit=20`, { headers: getHeaders() });
      setReminderLogs(toArray(res.data?.logs).filter((item) => item && typeof item === 'object'));
    } catch (error) {
      console.error('Error fetching reminder logs:', error);
      setReminderLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleMarkSent = async (logId) => {
    try {
      await axios.put(`${API}/reminder-logs/${logId}/mark-sent`, {}, { headers: getHeaders() });
      toast.success('Reminder ditandai terkirim');
      fetchReminderLogs();
    } catch (error) {
      toast.error('Gagal update status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ktc_token');
    localStorage.removeItem('ktc_user');
    navigate('/dadu/login');
  };

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data pengaturan..." />
      )}
      {/* Main Content */}
      <AppShell>
      <PageContainer className="cc-legacy-surface">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <StaffPageHeader
            eyebrow="Sistem"
            title={SETTINGS_TAB_META[activeTab]?.title || 'Pengaturan'}
            subtitle={SETTINGS_TAB_META[activeTab]?.subtitle || 'Konfigurasi sistem dan integrasi.'}
          />
        </motion.div>

        {/* FreeRADIUS Tab */}
        {activeTab === 'freeradius' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/20 flex items-center justify-center">
                  <Server className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Koneksi MySQL FreeRADIUS</h2>
                  <p className="text-slate-600 text-sm">Konfigurasi database FreeRADIUS untuk sync pelanggan aktif ke VPS atau tunnel lokal.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="staff-form-label">Host</label>
                  <input
                    type="text"
                    value={freeradiusSettings.host}
                    onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, host: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="localhost atau 127.0.0.1"
                    data-testid="input-fr-host"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Port</label>
                  <input
                    type="number"
                    value={freeradiusSettings.port}
                    onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, port: parseInt(e.target.value, 10) || 3306 }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="3306"
                    data-testid="input-fr-port"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Username</label>
                  <input
                    type="text"
                    value={freeradiusSettings.user}
                    onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, user: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="radius"
                    data-testid="input-fr-user"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Password</label>
                  <input
                    type="password"
                    value={freeradiusSettings.password}
                    onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="********"
                    data-testid="input-fr-password"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Database Name</label>
                  <input
                    type="text"
                    value={freeradiusSettings.database}
                    onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, database: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="radius"
                    data-testid="input-fr-database"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={freeradiusSettings.auto_sync}
                      onChange={(e) => setFreeradiusSettings((prev) => ({ ...prev, auto_sync: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                      data-testid="input-fr-autosync"
                    />
                    <span className="text-slate-600">Auto sync saat data pelanggan berubah status / PPPoE</span>
                  </label>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 mt-6">
                <div className="rounded-xl border border-[#cbd5e1] bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-slate-900 font-semibold">FreeRADIUS Runtime Status</p>
                      <p className="text-xs text-slate-500 mt-1">Status connector MySQL di backend lokal.</p>
                    </div>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={fetchFreeradiusStatus} disabled={loadingFreeradiusStatus}>
                      {loadingFreeradiusStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cek'}
                    </Button>
                  </div>
                  <div className="mt-3 text-sm">
                    {freeradiusStatus ? (
                      <>
                        <p className={freeradiusStatus.mysql_available ? 'text-green-400' : 'text-red-400'}>{freeradiusStatus.message}</p>
                        <p className="text-xs text-slate-500 mt-2">Jika backend lokal dan MySQL radius di VPS, gunakan host `127.0.0.1` dengan port tunnel lokal.</p>
                      </>
                    ) : (
                      <p className="text-slate-500">Status connector belum dicek.</p>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-[#cbd5e1] bg-white p-4">
                  <p className="text-slate-900 font-semibold">Mode Development Lokal</p>
                  <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal list-inside">
                    <li>Buka SSH tunnel ke VPS.</li>
                    <li>Isi koneksi ini dengan `127.0.0.1` dan port tunnel.</li>
                    <li>Test koneksi lalu simpan.</li>
                    <li>Jalankan sync awal sekali.</li>
                  </ol>
                </div>
              </div>

              <div className="grid lg:grid-cols-1 gap-4 mt-4">
                <div className="rounded-xl border border-[#cbd5e1] bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-slate-900 font-semibold">Rekonsiliasi Database VPS</p>
                      <p className="text-xs text-slate-500 mt-1">Sinkronkan relasi pelanggan/billing jika ada data tidak konsisten.</p>
                    </div>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleReconcileDb} disabled={reconcilingDb}>
                      {reconcilingDb ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Jalankan'}
                    </Button>
                  </div>
                  {reconcileDbResult ? (
                    <div className="mt-3 text-xs text-slate-600 space-y-1">
                      <p>{reconcileDbResult.message || 'Selesai'}</p>
                      {'customers_checked' in reconcileDbResult && <p>Pelanggan: {Number(reconcileDbResult.customers_checked || 0)} dicek, {Number(reconcileDbResult.customers_updated || 0)} diupdate</p>}
                      {'billings_checked' in reconcileDbResult && <p>Tagihan: {Number(reconcileDbResult.billings_checked || 0)} dicek, {Number(reconcileDbResult.billings_updated || 0)} diupdate</p>}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500">Belum ada proses rekonsiliasi dijalankan.</p>
                  )}
                </div>
              </div>

              {testResult && (
                <div className={`mt-6 p-4 rounded-lg flex items-center gap-3 ${
                  testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                    {testResult.message}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                <Button
                  onClick={handleTestConnection}
                  disabled={testing}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="btn-test-connection"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Test Koneksi
                </Button>
                <Button
                  onClick={handleSaveFreeradius}
                  disabled={loading}
                  className="bg-brand-blue hover:bg-brand-blue/90"
                  data-testid="btn-save-freeradius"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Simpan Koneksi
                </Button>
                <Button
                  onClick={handleSyncFreeRadius}
                  disabled={syncingFreeRadius}
                  variant="outline"
                  className="btn-secondary"
                >
                  {syncingFreeRadius ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Sync FreeRADIUS
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleApplyTunnelToConnection}>
                  <Server className="w-4 h-4 mr-2" />
                  Pakai Port Tunnel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => handleApplyEnvOnServer('all')}
                  disabled={applyingEnvTarget === 'all'}
                >
                  {applyingEnvTarget === 'all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Apply Semua ke .env VPS
                </Button>
              </div>

              {syncResult && (
                <div className={`mt-4 rounded-lg border p-4 ${Array.isArray(syncResult.errors) && syncResult.errors.length > 0 ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-green-500/30 bg-green-500/10'}`}>
                  <p className="text-slate-900 font-semibold">Hasil Sync FreeRADIUS</p>
                  <p className="text-sm text-slate-600 mt-1">{syncResult.message || 'Sinkronisasi selesai'}</p>
                  {'synced' in syncResult && <p className="text-sm text-cyan-300 mt-1">User tersinkron: {Number(syncResult.synced || 0)}</p>}
                  {Array.isArray(syncResult.errors) && syncResult.errors.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {syncResult.errors.slice(0, 5).map((item, index) => (
                      <p key={`${item.username || 'error'}-${index}`} className="text-xs text-yellow-100">{item.username ? `${item.username}: ` : ''}{item.error || 'Error tidak diketahui'}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid xl:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">WhatsApp Meta API</h2>
                  <p className="text-slate-600 text-sm">Pengaturan API resmi Meta dan antrean notifikasi</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="staff-form-label">API Version</label>
                  <input type="text" value={whatsappMetaSettings.api_version || 'v21.0'} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, api_version: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="v21.0" />
                </div>
                <div>
                  <label className="staff-form-label">Phone Number ID</label>
                  <input type="text" value={whatsappMetaSettings.phone_number_id || ''} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, phone_number_id: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="Phone Number ID" />
                </div>
                <div className="md:col-span-2">
                  <label className="staff-form-label">Access Token</label>
                  <input type="password" value={whatsappMetaSettings.access_token || ''} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, access_token: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="Access Token" />
                </div>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, enabled: e.target.checked }))} className="w-5 h-5" />Aktifkan WhatsApp Meta</label>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.reminder_enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} className="w-5 h-5" />Reminder WA</label>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.thank_you_enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, thank_you_enabled: e.target.checked }))} className="w-5 h-5" />Thank-you WA</label>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSaveWhatsappMeta} disabled={savingWhatsappMeta} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900">{savingWhatsappMeta ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Simpan WhatsApp Meta</Button>
                <Button onClick={handleEnqueueDueReminders} disabled={enqueueingReminder} variant="outline" className="btn-secondary">{enqueueingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}Antrekan Reminder</Button>
                <Button onClick={handleProcessWhatsappQueue} disabled={processingQueue} variant="outline" className="btn-secondary">{processingQueue ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Proses Queue WA</Button>
              </div>
              {queueProcessResult && <p className="text-emerald-300 text-sm mt-3">Processed: {queueProcessResult.processed || 0} | Sent: {queueProcessResult.sent || 0} | Failed: {queueProcessResult.failed || 0}</p>}
            </div>
            {ENABLE_FREERADIUS_MAPPING_UI && (
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
                <div className="flex items-center justify-between gap-3 mb-6">
                  <div>
                    <h2 className="font-outfit text-xl font-semibold text-slate-900">Mapping Paket FreeRADIUS</h2>
                    <p className="text-slate-600 text-sm">Atur rate-limit per paket dan attribute reply yang akan ditulis ke `radreply`.</p>
                  </div>
                  <Button type="button" variant="outline" className="btn-secondary" onClick={handleAddMappingRow}>
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Mapping
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="staff-form-label">Reply Attribute</label>
                    <input
                      type="text"
                      value={freeradiusMappings.reply_attribute}
                      onChange={(e) => setFreeradiusMappings((prev) => ({ ...prev, reply_attribute: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                      placeholder="Mikrotik-Rate-Limit"
                    />
                  </div>
                  <div>
                    <label className="staff-form-label">Operator</label>
                    <input
                      type="text"
                      value={freeradiusMappings.reply_operator}
                      onChange={(e) => setFreeradiusMappings((prev) => ({ ...prev, reply_operator: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                      placeholder=":="
                    />
                  </div>
                </div>

                <label className="inline-flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={freeradiusMappings.prefer_package_speed}
                    onChange={(e) => setFreeradiusMappings((prev) => ({ ...prev, prefer_package_speed: e.target.checked }))}
                    className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                  />
                  <span className="text-slate-600 text-sm">Prioritaskan `package_speed` / `package_name` pelanggan jika mengandung angka speed</span>
                </label>

                <div className="space-y-3 max-h-[32rem] overflow-y-auto pr-1">
                  {toArray(freeradiusMappings.items).map((item, index) => (
                    <div key={item.id} className="rounded-lg border border-[#cbd5e1] bg-white p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <p className="text-sm font-semibold text-slate-900">Mapping #{index + 1}</p>
                        <div className="flex items-center gap-3">
                          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={item.enabled !== false}
                              onChange={(e) => handleUpdateMappingRow(item.id, 'enabled', e.target.checked)}
                              className="w-4 h-4 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                            />
                            Aktif
                          </label>
                          <Button type="button" size="sm" variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-500/10" onClick={() => handleRemoveMappingRow(item.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-3">
                        <div>
                          <label className="staff-form-label-xs">Kode Paket</label>
                          <input
                            type="text"
                            value={item.package_key}
                            onChange={(e) => handleUpdateMappingRow(item.id, 'package_key', e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                            placeholder="family"
                          />
                        </div>
                        <div>
                          <label className="staff-form-label-xs">Nama Paket</label>
                          <input
                            type="text"
                            value={item.package_name}
                            onChange={(e) => handleUpdateMappingRow(item.id, 'package_name', e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                            placeholder="Family 20 Mbps"
                          />
                        </div>
                        <div>
                          <label className="staff-form-label-xs">Rate Limit</label>
                          <input
                            type="text"
                            value={item.rate_limit}
                            onChange={(e) => handleUpdateMappingRow(item.id, 'rate_limit', e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                            placeholder="20M/20M"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-4 text-xs text-slate-500">Jika `prioritaskan package_speed` aktif, pelanggan dengan `package_speed` seperti `20 Mbps` akan memakai `20M/20M` lebih dulu. Mapping manual dipakai saat speed tidak tersedia atau opsi ini dimatikan.</p>

                <div className="flex flex-wrap gap-3 mt-6">
                  <Button onClick={handleSaveFreeradiusMappings} disabled={savingMappings} className="bg-brand-blue hover:bg-brand-blue/90">
                    {savingMappings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Simpan Mapping
                  </Button>
                </div>
              </div>
              )}

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                      <Server className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-xl font-semibold text-slate-900">Generator SSH Tunnel</h2>
                      <p className="text-slate-600 text-sm">Simpan parameter tunnel VPS lalu copy command PowerShell yang siap dijalankan.</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="staff-form-label">SSH User</label>
                      <input
                        type="text"
                        value={freeradiusTunnel.ssh_user}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, ssh_user: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="root"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">SSH Host / VPS IP</label>
                      <input
                        type="text"
                        value={freeradiusTunnel.ssh_host}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, ssh_host: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="203.0.113.10"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">SSH Port</label>
                      <input
                        type="number"
                        value={freeradiusTunnel.ssh_port}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, ssh_port: parseInt(e.target.value, 10) || 22 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="22"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Local Port</label>
                      <input
                        type="number"
                        value={freeradiusTunnel.local_port}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, local_port: parseInt(e.target.value, 10) || 3307 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="3307"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Remote DB Host</label>
                      <input
                        type="text"
                        value={freeradiusTunnel.remote_db_host}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, remote_db_host: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="127.0.0.1"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Remote DB Port</label>
                      <input
                        type="number"
                        value={freeradiusTunnel.remote_db_port}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, remote_db_port: parseInt(e.target.value, 10) || 3306 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="3306"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="staff-form-label">PuTTY Session Name (Opsional)</label>
                      <input
                        type="text"
                        value={freeradiusTunnel.session_name}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, session_name: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="Nama session yang tersimpan di PuTTY"
                      />
                      <p className="text-xs text-slate-500 mt-2">Jika diisi, generator akan memakai `plink -load` dan field SSH User / Host / Identity File menjadi opsional.</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="staff-form-label">Identity File (Opsional)</label>
                      <input
                        type="text"
                        value={freeradiusTunnel.identity_file}
                        onChange={(e) => setFreeradiusTunnel((prev) => ({ ...prev, identity_file: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="C:/Users/Anda/.ssh/id_rsa"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="staff-form-label">Command PowerShell</label>
                    <textarea
                      readOnly
                      value={tunnelCommand}
                      className="w-full min-h-[120px] px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-700 focus:outline-none resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-2">Jalankan command ini dari root project. Tombol `Pakai Port Tunnel` akan mengisi host `127.0.0.1` dan port sesuai `Local Port` di atas.</p>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-6">
                    <Button onClick={handleSaveFreeradiusTunnel} disabled={savingTunnel} className="bg-brand-blue hover:bg-brand-blue/90">
                      {savingTunnel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Simpan Generator
                    </Button>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleCopyTunnelCommand}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Command
                    </Button>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleApplyTunnelToConnection}>
                      <Server className="w-4 h-4 mr-2" />
                      Pakai Port Tunnel
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                      <Database className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-xl font-semibold text-slate-900">Generator Konektor FreeRADIUS</h2>
                      <p className="text-slate-600 text-sm">Untuk kasus database FreeRADIUS di server terpisah: pilih direct atau SSH tunnel service.</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="staff-form-label">Mode Koneksi</label>
                      <select
                        value={freeradiusConnector.mode}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, mode: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                      >
                        <option value="ssh_tunnel">SSH Tunnel (Disarankan)</option>
                        <option value="direct">Direct Database</option>
                      </select>
                    </div>
                    <div>
                      <label className="staff-form-label">OS Server</label>
                      <select
                        value={freeradiusConnector.os_family}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, os_family: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                      >
                        <option value="ubuntu">Ubuntu / Debian</option>
                        <option value="rhel">CentOS / Rocky / Alma</option>
                      </select>
                    </div>
                    <div>
                      <label className="staff-form-label">SSH User</label>
                      <input
                        type="text"
                        value={freeradiusConnector.ssh_user}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, ssh_user: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="boncux"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">SSH Host</label>
                      <input
                        type="text"
                        value={freeradiusConnector.ssh_host}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, ssh_host: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="103.197.190.29"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">SSH Port</label>
                      <input
                        type="number"
                        value={freeradiusConnector.ssh_port}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, ssh_port: parseInt(e.target.value, 10) || 22 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="22"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Local Tunnel Port</label>
                      <input
                        type="number"
                        value={freeradiusConnector.local_port}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, local_port: parseInt(e.target.value, 10) || 3307 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="3307"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Remote DB Host</label>
                      <input
                        type="text"
                        value={freeradiusConnector.remote_db_host}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, remote_db_host: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="127.0.0.1"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">Remote DB Port</label>
                      <input
                        type="number"
                        value={freeradiusConnector.remote_db_port}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, remote_db_port: parseInt(e.target.value, 10) || 3306 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="3306"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="staff-form-label">Identity File SSH</label>
                      <input
                        type="text"
                        value={freeradiusConnector.identity_file}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, identity_file: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="/home/boncu/.ssh/ktc_vps_tunnel"
                        disabled={freeradiusConnector.mode !== 'ssh_tunnel'}
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">RADIUS Host (online)</label>
                      <input
                        type="text"
                        value={freeradiusConnector.radius_host_online}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, radius_host_online: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="127.0.0.1"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">RADIUS Port (online)</label>
                      <input
                        type="number"
                        value={freeradiusConnector.radius_port_online}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, radius_port_online: parseInt(e.target.value, 10) || 3307 }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="3307"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">DB Name</label>
                      <input
                        type="text"
                        value={freeradiusConnector.radius_database}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, radius_database: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="radius"
                      />
                    </div>
                    <div>
                      <label className="staff-form-label">DB User</label>
                      <input
                        type="text"
                        value={freeradiusConnector.radius_user}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, radius_user: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="radius"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="staff-form-label">DB Password</label>
                      <input
                        type="password"
                        value={freeradiusConnector.radius_password}
                        onChange={(e) => setFreeradiusConnector((prev) => ({ ...prev, radius_password: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                        placeholder="password radius"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="staff-form-label">Script Setup FreeRADIUS</label>
                    <textarea
                      readOnly
                      value={freeradiusConnectorScript}
                      className="w-full min-h-[220px] px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-700 focus:outline-none resize-none"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="staff-form-label">.env Snippet FreeRADIUS</label>
                    <textarea
                      readOnly
                      value={freeradiusEnvSnippet}
                      className="w-full min-h-[120px] px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-700 focus:outline-none resize-none"
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 mt-6">
                    <Button onClick={handleSaveFreeradiusConnector} disabled={savingFreeradiusConnector} className="bg-brand-blue hover:bg-brand-blue/90">
                      {savingFreeradiusConnector ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Simpan Generator
                    </Button>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleCopyFreeradiusScript}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Script
                    </Button>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleApplyFreeradiusConnectorToConnection}>
                      <Server className="w-4 h-4 mr-2" />
                      Terapkan ke Koneksi
                    </Button>
                    <Button type="button" variant="outline" className="btn-secondary" onClick={handleCopyFreeradiusEnvSnippet}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy .env FreeRADIUS
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="btn-secondary"
                      onClick={() => handleApplyEnvOnServer('freeradius')}
                      disabled={applyingEnvTarget === 'freeradius'}
                    >
                      {applyingEnvTarget === 'freeradius' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                      Apply ke .env VPS
                    </Button>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
                      <Database className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-xl font-semibold text-slate-900">Backup Sistem</h2>
                      <p className="text-slate-600 text-sm">Ekspor atau impor backup JSON untuk pengaturan, pelanggan, FTTH, tiket, dan data operasional.</p>
                    </div>
                  </div>
                  <div className={`mb-4 rounded-lg border px-3 py-2 text-xs ${backupIsStale ? 'border-red-500/40 bg-red-500/10 text-red-300' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span>{loadingBackupStatus ? 'Mengecek status backup...' : `Backup terakhir: ${backupLastTime}`}</span>
                      {Number.isFinite(backupAgeHours) ? <span>Usia: {backupAgeHours.toFixed(1)} jam</span> : null}
                      {backupIsStale ? <span className="font-semibold">Warning: backup lebih dari 24 jam.</span> : null}
                    </div>
                  </div>

                  <input
                    ref={backupInputRef}
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={(event) => setBackupImportFile(event.target.files?.[0] || null)}
                  />

                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" className="btn-secondary" onClick={() => backupInputRef.current?.click()}>
                      <Database className="w-4 h-4 mr-2" />
                      Pilih Backup
                    </Button>
                    <Button
                      onClick={handleImportBackup}
                      disabled={backupImportLoading || !backupImportFile}
                      variant="outline"
                      className="btn-secondary"
                    >
                      {backupImportLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Import Backup
                    </Button>
                    <Button
                      onClick={handleExportBackup}
                      disabled={backupLoading}
                      variant="outline"
                      className="btn-secondary"
                    >
                      {backupLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Download Backup
                    </Button>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    {backupImportFile ? `File backup terpilih: ${backupImportFile.name}` : 'Belum ada file backup yang dipilih.'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* GenieACS Tab */}
        {activeTab === 'genieacs' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-slate-900 font-semibold">GenieACS Bridge Status</p>
                  <p className="text-xs text-slate-500 mt-1">Status koneksi backend online ke GenieACS.</p>
                </div>
                <Button type="button" variant="outline" className="btn-secondary" onClick={fetchGenieacsHealth} disabled={loadingGenieacsHealth}>
                  {loadingGenieacsHealth ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cek'}
                </Button>
              </div>
              <div className="mt-3 text-sm">
                {genieacsHealth ? (
                  <>
                    <p className={(genieacsHealth.connected || genieacsHealth.available) ? 'text-green-400' : 'text-red-400'}>
                      {(genieacsHealth.connected || genieacsHealth.available) ? 'GenieACS terhubung' : (genieacsHealth.error || genieacsHealth.message || 'GenieACS tidak tersedia')}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">Endpoint: {genieacsHealth.base_url || genieacsHealth.url || '-'}</p>
                  </>
                ) : (
                  <p className="text-slate-500">Status GenieACS belum dicek.</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <Network className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Generator Konektor GenieACS</h2>
                  <p className="text-slate-600 text-sm">Pilih OS server GenieACS dan mode koneksi, lalu copy script siap tempel.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="staff-form-label">Mode Koneksi</label>
                  <select
                    value={genieacsConnector.mode}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, mode: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                  >
                    <option value="reverse_tunnel">Reverse Tunnel (Disarankan)</option>
                    <option value="direct_private">Direct Private URL (via VPN site)</option>
                  </select>
                </div>
                <div>
                  <label className="staff-form-label">OS Server GenieACS</label>
                  <select
                    value={genieacsConnector.os_family}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, os_family: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                  >
                    <option value="ubuntu">Ubuntu / Debian</option>
                    <option value="rhel">CentOS / Rocky / Alma</option>
                  </select>
                </div>
                <div>
                  <label className="staff-form-label">SSH User VPS</label>
                  <input
                    type="text"
                    value={genieacsConnector.ssh_user}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, ssh_user: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="boncux"
                    disabled={genieacsConnector.mode !== 'reverse_tunnel'}
                  />
                </div>
                <div>
                  <label className="staff-form-label">SSH Host VPS</label>
                  <input
                    type="text"
                    value={genieacsConnector.ssh_host}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, ssh_host: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="103.197.190.29"
                    disabled={genieacsConnector.mode !== 'reverse_tunnel'}
                  />
                </div>
                <div>
                  <label className="staff-form-label">URL GENIEACS_URL di backend online</label>
                  <input
                    type="text"
                    value={genieacsConnector.genieacs_url_online}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, genieacs_url_online: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="http://127.0.0.1:27567"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Remote Port di VPS</label>
                  <input
                    type="number"
                    value={genieacsConnector.reverse_remote_port}
                    onChange={(e) => setGenieacsConnector((prev) => ({ ...prev, reverse_remote_port: parseInt(e.target.value, 10) || 27567 }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="27567"
                    disabled={genieacsConnector.mode !== 'reverse_tunnel'}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="staff-form-label">Script Setup (copy paste)</label>
                <textarea
                  readOnly
                  value={genieacsConnectorScript}
                  className="w-full min-h-[220px] px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-700 focus:outline-none resize-none"
                />
              </div>
              <div className="mt-4">
                <label className="staff-form-label">.env Snippet GenieACS</label>
                <textarea
                  readOnly
                  value={genieacsEnvSnippet}
                  className="w-full min-h-[120px] px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-700 focus:outline-none resize-none"
                />
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSaveGenieacsConnector} disabled={savingGenieacsConnector} className="bg-brand-blue hover:bg-brand-blue/90">
                  {savingGenieacsConnector ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Generator
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleCopyGenieacsScript}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Script
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleApplyGenieacsUrl}>
                  <Server className="w-4 h-4 mr-2" />
                  Isi URL Otomatis
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleCopyGenieacsEnvSnippet}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy .env GenieACS
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => handleApplyEnvOnServer('genieacs')}
                  disabled={applyingEnvTarget === 'genieacs'}
                >
                  {applyingEnvTarget === 'genieacs' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                  Apply ke .env VPS
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* OpenNMS Tab */}
        {activeTab === 'opennms' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-slate-900 font-semibold">OpenNMS Bridge Status</p>
                  <p className="text-xs text-slate-500 mt-1">Status koneksi backend online ke OpenNMS.</p>
                </div>
                <Button type="button" variant="outline" className="btn-secondary" onClick={fetchOpennmsHealth} disabled={loadingOpennmsHealth}>
                  {loadingOpennmsHealth ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cek'}
                </Button>
              </div>
              <div className="mt-3 text-sm">
                {opennmsHealth ? (
                  <>
                    <p className={opennmsHealth.connected ? 'text-green-400' : 'text-red-400'}>
                      {opennmsHealth.connected ? 'OpenNMS terhubung' : (opennmsHealth.message || opennmsHealth.error || 'OpenNMS tidak terhubung')}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">Endpoint: {opennmsHealth.base_url || '-'}</p>
                  </>
                ) : (
                  <p className="text-slate-500">Status OpenNMS belum dicek.</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                  <Network className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Konfigurasi OpenNMS</h2>
                  <p className="text-slate-600 text-sm">Isi koneksi OpenNMS untuk integrasi observabilitas.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="staff-form-label">Base URL OpenNMS</label>
                  <input
                    type="text"
                    value={opennmsSettings.base_url}
                    onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, base_url: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="https://opennms.example.com"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Username</label>
                  <input
                    type="text"
                    value={opennmsSettings.username}
                    onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, username: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="admin"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Password</label>
                  <input
                    type="password"
                    value={opennmsSettings.password}
                    onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="��������"
                  />
                </div>
                <div>
                  <label className="staff-form-label">Timeout (detik)</label>
                  <input
                    type="number"
                    min={3}
                    value={opennmsSettings.timeout_seconds}
                    onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, timeout_seconds: parseInt(e.target.value, 10) || 8 }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer text-slate-600">
                    <input
                      type="checkbox"
                      checked={Boolean(opennmsSettings.enabled)}
                      onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                      className="w-5 h-5"
                    />
                    Aktifkan Integrasi OpenNMS
                  </label>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer text-slate-600">
                    <input
                      type="checkbox"
                      checked={Boolean(opennmsSettings.verify_tls)}
                      onChange={(e) => setOpennmsSettings((prev) => ({ ...prev, verify_tls: e.target.checked }))}
                      className="w-5 h-5"
                    />
                    Verifikasi TLS/SSL
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSaveOpennmsSettings} disabled={savingOpennmsSettings} className="bg-brand-blue hover:bg-brand-blue/90">
                  {savingOpennmsSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Pengaturan
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={fetchOpennmsHealth} disabled={loadingOpennmsHealth}>
                  {loadingOpennmsHealth ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                  Test Koneksi
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Reminder Tab */}
        {/* Reminder Tab */}
        {activeTab === 'whatsapp' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-xl font-semibold text-slate-900">WhatsApp Meta API</h2>
                    <p className="text-slate-600 text-sm">Pusat konfigurasi API WhatsApp untuk Live Chat dan otomasi support.</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Link to="/dadu/whatsapp-live" className="inline-flex items-center gap-2 rounded-lg border border-[#cbd5e1] bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    Buka WhatsApp Live Chat
                  </Link>
                  <Link to="/dadu/whatsapp-templates" className="inline-flex items-center gap-2 rounded-lg border border-brand-blue/40 bg-brand-blue/10 px-3 py-2 text-sm text-brand-blue hover:bg-brand-blue/20">
                    Buka Template WhatsApp
                  </Link>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="staff-form-label">API Version</label>
                  <input type="text" value={whatsappMetaSettings.api_version || 'v21.0'} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, api_version: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="v21.0" />
                </div>
                <div>
                  <label className="staff-form-label">Phone Number ID</label>
                  <input type="text" value={whatsappMetaSettings.phone_number_id || ''} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, phone_number_id: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="Phone Number ID" />
                </div>
                <div className="md:col-span-2">
                  <label className="staff-form-label">Access Token</label>
                  <input type="password" value={whatsappMetaSettings.access_token || ''} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, access_token: e.target.value }))} className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900" placeholder="Access Token" />
                </div>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, enabled: e.target.checked }))} className="w-5 h-5" />Aktifkan WhatsApp Meta</label>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.reminder_enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} className="w-5 h-5" />Reminder WA</label>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(whatsappMetaSettings.thank_you_enabled)} onChange={(e) => setWhatsappMetaSettings((prev) => ({ ...prev, thank_you_enabled: e.target.checked }))} className="w-5 h-5" />Thank-you WA</label>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSaveWhatsappMeta} disabled={savingWhatsappMeta} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900">
                  {savingWhatsappMeta ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan WhatsApp Meta
                </Button>
                <Button onClick={handleEnqueueDueReminders} disabled={enqueueingReminder} variant="outline" className="btn-secondary">
                  {enqueueingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                  Antrekan Reminder
                </Button>
                <Button onClick={handleProcessWhatsappQueue} disabled={processingQueue} variant="outline" className="btn-secondary">
                  {processingQueue ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Proses Queue WA
                </Button>
              </div>
              {queueProcessResult && <p className="text-emerald-300 text-sm mt-3">Processed: {queueProcessResult.processed || 0} | Sent: {queueProcessResult.sent || 0} | Failed: {queueProcessResult.failed || 0}</p>}
            </div>
          </motion.div>
        )}

        {activeTab === 'reminder' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Settings Card */}
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Pengaturan Reminder</h2>
                  <p className="text-slate-600 text-sm">Konfigurasi pengingat tagihan otomatis via WhatsApp</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reminderSettings.enabled}
                      onChange={(e) => setReminderSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                      data-testid="input-rm-enabled"
                    />
                    <span className="text-slate-600">Aktifkan auto reminder</span>
                  </label>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reminderSettings.whatsapp_enabled}
                      onChange={(e) => setReminderSettings(prev => ({ ...prev, whatsapp_enabled: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                      data-testid="input-rm-whatsapp"
                    />
                    <span className="text-slate-600">Kirim via WhatsApp</span>
                  </label>
                </div>
                <div>
                  <label className="staff-form-label">Hari Sebelum Jatuh Tempo</label>
                  <input
                    type="number"
                    value={reminderSettings.days_before_due}
                    onChange={(e) => setReminderSettings(prev => ({ ...prev, days_before_due: parseInt(e.target.value) || 3 }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    min="1"
                    max="14"
                    data-testid="input-rm-days"
                  />
                  <p className="text-slate-500 text-xs mt-1">Reminder dikirim H-{reminderSettings.days_before_due} sebelum jatuh tempo</p>
                </div>
                <div>
                  <label className="staff-form-label">Nomor Admin (opsional)</label>
                  <input
                    type="text"
                    value={reminderSettings.admin_phone}
                    onChange={(e) => setReminderSettings(prev => ({ ...prev, admin_phone: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                    placeholder="08123456789"
                    data-testid="input-rm-phone"
                  />
                  <p className="text-slate-500 text-xs mt-1">Untuk notifikasi ke admin</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <input
                  ref={backupInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(event) => setBackupImportFile(event.target.files?.[0] || null)}
                />
                <Button
                  onClick={handleSaveReminder}
                  disabled={loading}
                  className="bg-brand-blue hover:bg-brand-blue/90"
                  data-testid="btn-save-reminder"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Simpan Pengaturan
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Kebijakan Suspend Overdue</h2>
                  <p className="text-slate-600 text-sm">Atur grace period suspend tagihan overdue dan aktifkan kembali pelanggan otomatis setelah tunggakan bersih.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={billingOverduePolicy.auto_suspend_enabled}
                      onChange={(e) => setBillingOverduePolicy((prev) => ({ ...prev, auto_suspend_enabled: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                    />
                    <span className="text-slate-600">Suspend otomatis pelanggan yang tagihannya overdue</span>
                  </label>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={billingOverduePolicy.auto_reactivate_on_clear}
                      onChange={(e) => setBillingOverduePolicy((prev) => ({ ...prev, auto_reactivate_on_clear: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                    />
                    <span className="text-slate-600">Aktifkan kembali otomatis setelah semua overdue lunas</span>
                  </label>
                </div>
                <div>
                  <label className="staff-form-label">Grace Period Suspend (hari)</label>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={billingOverduePolicy.suspend_after_days}
                    onChange={(e) => setBillingOverduePolicy((prev) => ({ ...prev, suspend_after_days: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                    className="w-full px-4 py-3 bg-white border border-[#cbd5e1] rounded-lg text-slate-900 focus:border-brand-blue focus:outline-none"
                  />
                  <p className="text-slate-500 text-xs mt-1">Pelanggan aktif baru disuspend setelah melewati jatuh tempo + {billingOverduePolicy.suspend_after_days} hari.</p>
                </div>
                <div className="rounded-lg border border-[#cbd5e1] bg-white p-4">
                  <p className="text-sm font-semibold text-slate-900">Catatan Operasional</p>
                  <p className="text-xs text-slate-600 mt-2">Reaktivasi otomatis hanya berlaku untuk pelanggan yang sebelumnya disuspend otomatis oleh billing, bukan suspend manual admin.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSaveBillingOverduePolicy} disabled={savingBillingPolicy} className="bg-brand-blue hover:bg-brand-blue/90">
                  {savingBillingPolicy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Kebijakan
                </Button>
              </div>
            </div>

            {/* Scheduler Control Card */}
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Scheduler Otomatis</h2>
                  <p className="text-slate-600 text-sm">Jalankan pengecekan reminder secara otomatis setiap hari jam 8 pagi</p>
                </div>
              </div>

              {/* Scheduler Status */}
              <div className={`p-4 rounded-lg mb-6 flex items-center gap-3 ${
                schedulerStatus.running 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-gray-500/10 border border-gray-500/30'
              }`}>
                <div className={`w-3 h-3 rounded-full ${schedulerStatus.running ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                <div className="flex-1">
                  <p className={schedulerStatus.running ? 'text-green-400 font-medium' : 'text-slate-600 font-medium'}>
                    {schedulerStatus.running ? 'Scheduler Aktif' : 'Scheduler Tidak Aktif'}
                  </p>
                  {schedulerStatus.running && nextSchedulerJob && (
                    <p className="text-slate-500 text-sm">
                      Next run: {formatDateTime(nextSchedulerJob.next_run)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {!schedulerStatus.running ? (
                  <Button
                    onClick={handleStartScheduler}
                    disabled={schedulerLoading}
                    className="bg-green-500 hover:bg-green-600 text-slate-900"
                    data-testid="btn-start-scheduler"
                  >
                    {schedulerLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Mulai Scheduler
                  </Button>
                ) : (
                  <Button
                    onClick={handleStopScheduler}
                    disabled={schedulerLoading}
                    variant="outline"
                    className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                    data-testid="btn-stop-scheduler"
                  >
                    {schedulerLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Square className="w-4 h-4 mr-2" />
                    )}
                    Hentikan Scheduler
                  </Button>
                )}
                <Button
                  onClick={handleRunNow}
                  disabled={schedulerLoading}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="btn-run-now"
                >
                  {schedulerLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Jalankan Sekarang
                </Button>
                <Button
                  onClick={fetchReminderLogs}
                  disabled={loadingLogs}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="btn-view-logs"
                >
                  {loadingLogs ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <History className="w-4 h-4 mr-2" />
                  )}
                  Lihat Riwayat
                </Button>
              </div>

              {/* Reminder Logs */}
              {reminderLogs.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-slate-900 font-medium mb-3">Riwayat Reminder</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {reminderLogs.map((log) => (
                      <div 
                        key={log.id}
                        className="p-3 rounded-lg bg-white border border-[#dbe7ff] flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <p className="text-slate-900 text-sm">{log.customer_name || '-'} - {log.invoice_number || '-'}</p>
                          <p className="text-slate-500 text-xs">
                            {formatDateTime(log.created_at)} | 
                            <span className={`ml-1 ${log.status === 'sent' ? 'text-green-400' : 'text-yellow-400'}`}>
                              {log.status === 'sent' ? 'Terkirim' : 'Pending'}
                            </span>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {log.whatsapp_url ? (
                            <a
                              href={log.whatsapp_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20"
                            >
                              <Send className="w-4 h-4" />
                            </a>
                          ) : (
                            <span className="p-2 rounded-lg bg-white/5 text-slate-500">
                              <Send className="w-4 h-4" />
                            </span>
                          )}
                          {log.status !== 'sent' && (
                            <button
                              onClick={() => handleMarkSent(log.id)}
                              className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Check Reminders Card */}
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Cek Tagihan Perlu Diingatkan</h2>
                  <p className="text-slate-600 text-sm">Lihat tagihan yang mendekati atau sudah melewati jatuh tempo</p>
                </div>
                <Button
                  onClick={handleCheckReminders}
                  disabled={loadingReminders}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="btn-check-reminders"
                >
                  {loadingReminders ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4 mr-2" />
                  )}
                  Cek Sekarang
                </Button>
              </div>

              {reminders.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {reminders.map((reminder) => (
                    <div 
                      key={reminder.billing_id}
                      className={`p-4 rounded-lg border ${
                        reminder.days_until_due <= 0 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-yellow-500/10 border-yellow-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-slate-900 font-medium">{reminder.customer_name}</p>
                          <p className="text-slate-600 text-sm">{reminder.invoice_number} - {reminder.phone}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-900 font-semibold">
                            Rp {reminder.amount?.toLocaleString('id-ID')}
                          </p>
                          <p className={`text-sm font-medium ${
                            reminder.days_until_due <= 0 ? 'text-red-400' : 'text-yellow-400'
                          }`}>
                            {reminder.status_text}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <a
                          href={reminder.whatsapp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-slate-900 text-sm rounded-lg transition-colors"
                          data-testid={`btn-wa-${reminder.billing_id}`}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          Kirim WhatsApp
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Klik "Cek Sekarang" untuk melihat tagihan yang perlu diingatkan
                </p>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Keamanan Sesi Login</h2>
                  <p className="text-slate-600 text-sm">Atur apakah 1 akun hanya boleh login di 1 perangkat (single-session).</p>
                </div>
              </div>

              <div className="rounded-xl border border-[#cbd5e1] bg-slate-50 p-4 space-y-3">
                <label className="flex items-center gap-3 text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(sessionSecurity.single_session_enabled)}
                    onChange={(e) => setSessionSecurity((prev) => ({ ...prev, single_session_enabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-slate-300 bg-white text-brand-blue focus:ring-brand-blue"
                  />
                  <span className="font-medium">Aktifkan Single-Session Login</span>
                </label>
                <p className="text-xs text-slate-500">
                  ON: akun staff dan portal pelanggan hanya bisa aktif di satu perangkat sekaligus.
                  OFF: akun boleh login bersamaan di beberapa perangkat.
                </p>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={handleSaveSessionSecurity} disabled={savingSessionSecurity} className="bg-brand-blue hover:bg-brand-blue/90">
                  {savingSessionSecurity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Pengaturan Keamanan
                </Button>
              </div>
            </div>
          </motion.div>
        )}
        {activeTab === 'payment' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Channel Transfer Manual</h2>
                  <p className="text-slate-600 text-sm">Atur rekening transfer dan toggle pembayaran manual.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(paymentChannels.transfer_enabled)} onChange={(e) => setPaymentChannels((prev) => ({ ...prev, transfer_enabled: e.target.checked }))} className="w-5 h-5" />Aktifkan Transfer Manual</label>
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(paymentChannels.simulate_mode)} onChange={(e) => setPaymentChannels((prev) => ({ ...prev, simulate_mode: e.target.checked }))} className="w-5 h-5" />Mode Simulasi (dev)</label>
              </div>

              <div className="mb-6">
                <p className="text-slate-900 font-semibold mb-3">Rekening Transfer</p>
                <div className="space-y-3">
                  {toArray(paymentChannels.bank_accounts).map((bank) => (
                    <div key={bank.id} className="rounded-lg border border-[#cbd5e1] bg-white p-3 grid md:grid-cols-5 gap-2">
                      <input type="text" value={bank.bank_name || ''} onChange={(e) => handleUpdateBankAccount(bank.id, 'bank_name', e.target.value)} className="px-3 py-2 bg-white border border-[#cbd5e1] rounded text-slate-900" placeholder="Bank" />
                      <input type="text" value={bank.account_number || ''} onChange={(e) => handleUpdateBankAccount(bank.id, 'account_number', e.target.value)} className="px-3 py-2 bg-white border border-[#cbd5e1] rounded text-slate-900" placeholder="Nomor Rekening" />
                      <input type="text" value={bank.account_name || ''} onChange={(e) => handleUpdateBankAccount(bank.id, 'account_name', e.target.value)} className="px-3 py-2 bg-white border border-[#cbd5e1] rounded text-slate-900" placeholder="Nama Pemilik" />
                      <input type="text" value={bank.note || ''} onChange={(e) => handleUpdateBankAccount(bank.id, 'note', e.target.value)} className="px-3 py-2 bg-white border border-[#cbd5e1] rounded text-slate-900" placeholder="Catatan" />
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={Boolean(bank.enabled)} onChange={(e) => handleUpdateBankAccount(bank.id, 'enabled', e.target.checked)} />Aktif</label>
                        <Button type="button" variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-500/10" onClick={() => handleRemoveBankAccount(bank.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" className="btn-secondary mt-3" onClick={handleAddBankAccount}>
                  <Plus className="w-4 h-4 mr-2" />Tambah Rekening
                </Button>
              </div>

            </div>

            <div className="bg-white rounded-xl p-6 border border-[#cbd5e1]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Network className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">Provider API Payment Gateway</h2>
                  <p className="text-slate-600 text-sm">Konfigurasi Duitku, Midtrans, Xendit, dan iPaymu.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-1 gap-4 mb-6">
                <label className="flex items-center gap-3 text-slate-600"><input type="checkbox" checked={Boolean(paymentChannels.gateway_enabled)} onChange={(e) => setPaymentChannels((prev) => ({ ...prev, gateway_enabled: e.target.checked }))} className="w-5 h-5" />Aktifkan Payment Gateway</label>
              </div>

              <div className="space-y-4">
                <p className="text-slate-900 font-semibold">Daftar Provider</p>
                {['duitku', 'midtrans', 'xendit', 'ipaymu'].map((providerKey) => {
                  const cfg = (paymentChannels.providers || {})[providerKey] || {};
                  return (
                    <div key={providerKey} className="rounded-lg border border-[#cbd5e1] bg-white p-4">
                      <label className="flex items-center gap-2 text-slate-900 font-medium mb-3">
                        <input type="checkbox" checked={Boolean(cfg.enabled)} onChange={(e) => handleUpdateGatewayProvider(providerKey, 'enabled', e.target.checked)} />
                        {providerKey.toUpperCase()}
                      </label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {Object.keys(cfg).filter((k) => k !== 'enabled').map((fieldKey) => (
                          <input
                            key={`${providerKey}-${fieldKey}`}
                            type={String(fieldKey).toLowerCase().includes('key') || String(fieldKey).toLowerCase().includes('token') ? 'password' : 'text'}
                            value={cfg[fieldKey] ?? ''}
                            onChange={(e) => handleUpdateGatewayProvider(providerKey, fieldKey, e.target.value)}
                            className="px-3 py-2 bg-white border border-[#cbd5e1] rounded text-slate-900"
                            placeholder={`${providerKey}.${fieldKey}`}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6">
                <Button onClick={handleSavePaymentChannels} disabled={savingPaymentChannels} className="bg-brand-blue hover:bg-brand-blue/90">
                  {savingPaymentChannels ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Pengaturan Payment
                </Button>
              </div>
            </div>
          </motion.div>
        )}

                  </PageContainer>
    </AppShell>
    </>
  );
};

export default AdminSettings;





































